import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Bell, Zap, BookOpen, Trophy, Users, UserPlus, UserMinus, Timer, Info, Volume2, VolumeX } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getRoundDetail } from '@/lib/api/endpoints/rounds';
import { queryKeys } from '@/lib/api/queryKeys';
import { useGameSocket } from '@/lib/socket/useGameSocket';
import { useRpsSocket } from '@/lib/socket/useRpsSocket';
import { gameStore } from '@/store/gameStore';
import { rpsStore } from '@/store/rpsStore';
import type { GameNotification } from '@/store/gameStore';
import { getSocket } from '@/lib/socket/client';
import { CLIENT_EVENTS, SERVER_EVENTS } from '@/lib/socket/events';
import { sessionStore } from '@/store/sessionStore';
import { Button } from '@/design-system/components/Button';
import { Avatar } from '@/design-system/components/Avatar';
import { t } from '@/lib/i18n';
import { useAudio } from '@/lib/audio/useAudio';
import { ChatPanel } from '@/features/chat/ChatPanel';
import styles from './WaitingRoomPage.module.css';

function useCountdownSec(startingAt: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startingAt) return;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [startingAt]);
  if (!startingAt) return null;
  return Math.max(0, Math.ceil((startingAt - now) / 1000));
}

export default function WaitingRoomPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const pin = (location.state as { pin?: string } | null)?.pin;

  if (!roundId) throw new Error('Missing roundId');

  const { data } = useQuery({
    queryKey: queryKeys.rounds.detail(roundId),
    queryFn: () => getRoundDetail(roundId),
  });

  useGameSocket(roundId, 'player', pin ?? undefined);
  useRpsSocket(roundId);

  // Navigate to play when game starts
  const phase = gameStore((s) => s.phase);
  const rpsPhase = rpsStore((s) => s.phase);
  const gameType = data?.round?.game_type;

  useEffect(() => {
    if (gameType === 'rock_paper_scissors') {
      // Chuyển ngay khi draw bắt đầu để tất cả user cùng xem màn hình bốc thăm
      if (rpsPhase === 'drawing' || rpsPhase === 'bracket_ready' || rpsPhase === 'round_active' || rpsPhase === 'round_transitioning') {
        navigate(`/rounds/${roundId}/tournament`, { replace: true });
      }
    } else {
      if (phase === 'question' || phase === 'reveal' || phase === 'word_chain') {
        navigate(`/rounds/${roundId}/play`, { replace: true });
      }
      if (phase === 'ended') {
        navigate(`/rounds/${roundId}/result`, { replace: true });
      }
    }
  }, [phase, rpsPhase, gameType, roundId, navigate]);

  // Listen for room_closed
  useEffect(() => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    const onRoomClosed = () => {
      navigate('/lobby', { replace: true });
    };
    socket.on(SERVER_EVENTS.ROOM_CLOSED, onRoomClosed);
    return () => { socket.off(SERVER_EVENTS.ROOM_CLOSED, onRoomClosed); };
  }, [navigate]);

  const participants = gameStore((s) => s.participants);
  const connectionStatus = gameStore((s) => s.connectionStatus);
  const notifications = gameStore((s) => s.notifications);
  const startingAt = gameStore((s) => s.startingAt);

  const [muted, setMuted] = useState(false);

  // Nhạc phòng chờ — tắt khi game bắt đầu
  useAudio('/audio/lobby.mp3', {
    enabled: phase === 'waiting' || phase === 'starting',
    volume: muted ? 0 : 0.35,
    fadeOutMs: 1000,
  });
  const round = data?.round;
  const countdown = useCountdownSec(startingAt);

  const handleLeave = () => {
    const token = sessionStore.getState().token;
    if (token) {
      const socket = getSocket(token);
      socket.emit(CLIENT_EVENTS.LEAVE_ROOM, { round_id: roundId });
    }
    gameStore.getState().reset();
    navigate('/lobby', { replace: true });
  };

  return (
    <div className={styles.root}>
      {connectionStatus === 'reconnecting' && (
        <div className={styles.connBanner}>{t('common.connecting')}</div>
      )}

      {/* Countdown overlay */}
      {startingAt && countdown !== null && (
        <div className={styles.countdownOverlay}>
          <div className={styles.countdownBox}>
            <div className={styles.countdownLabel}>{t('waitingRoom.countdownOverlay')}</div>
            <div className={styles.countdownNum}>{countdown}</div>
            <div className={styles.countdownSub}>{t('waitingRoom.countdownUnit')}</div>
          </div>
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.headerTop}>
              <h1 className={styles.roundName}>{round?.name ?? t('waitingRoom.fallbackRoundName')}</h1>
              <button
                className={styles.muteBtn}
                onClick={() => setMuted((m) => !m)}
                title={muted ? t('roundControl.unmuteMusic') : t('roundControl.muteMusic')}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
            {round?.description && <p className={styles.roundDesc}>{round.description}</p>}
          </div>

          {round?.pin_code && (
            <div className={styles.pinDisplay}>
              <span className={styles.pinLabel}>{t('waitingRoom.pinLabel')}</span>
              <span className={styles.pinCode}>{round.pin_code}</span>
            </div>
          )}

          {round?.game_type !== 'rock_paper_scissors' && round?.game_type !== 'word_chain' && (
            <div className={styles.configRow}>
              <div className={styles.configItem}>
                <span className={styles.configLabel}>{t('waitingRoom.configQuestions')}</span>
                <span className={styles.configValue}>{round?.number_of_questions ?? '?'}</span>
              </div>
              <div className={styles.configItem}>
                <span className={styles.configLabel}>{t('waitingRoom.configTime')}</span>
                <span className={styles.configValue}>{t('waitingRoom.timeValue', { n: round?.time_limit_per_question_sec ?? '?' })}</span>
              </div>
            </div>
          )}

          {round?.game_type && (
            <div className={styles.gameInfoPanel}>
              {round.game_type === 'rock_paper_scissors' ? (
                <>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="blue"><Zap size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Kéo Búa Bao Tournament</div>
                      <p className={styles.gameInfoText}>Đấu loại trực tiếp 1 vs 1. Thắng tiếp tục, thua bị loại.</p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="green"><BookOpen size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Cách chơi</div>
                      <p className={styles.gameInfoText}>Chọn ✊ Búa, ✋ Bao hoặc ✌️ Kéo trước khi hết thời gian. Búa thắng Kéo, Kéo thắng Bao, Bao thắng Búa.</p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="gold"><Trophy size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Xếp hạng</div>
                      <p className={styles.gameInfoText}>Cùng vòng loại = cùng hạng. Người vô địch chiếm hạng 1.</p>
                    </div>
                  </div>
                </>
              ) : round.game_type === 'word_chain' ? (
                <>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="blue"><Zap size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Nối Từ — Giới thiệu</div>
                      <p className={styles.gameInfoText}>
                        Một chuỗi gợi ý sẽ xuất hiện trên màn hình. Mỗi gợi ý là một từ bị ẩn — chữ cái đầu được hiển thị, các chữ còn lại là dấu gạch dưới (<strong>T___</strong>).
                      </p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="green"><BookOpen size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Luật chơi</div>
                      <p className={styles.gameInfoText}>
                        Điền vào ô trống ứng với từng gợi ý để hoàn thành chuỗi từ. Bạn có thể điền theo thứ tự bất kỳ. Nhấn <strong>Gửi</strong> một lần duy nhất trước khi hết giờ — không được sửa sau khi gửi.
                      </p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="gold"><Trophy size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>Tính điểm</div>
                      <p className={styles.gameInfoText}>
                        Mỗi từ đúng được tính điểm riêng. Gửi sớm hơn nhận thêm điểm thưởng tốc độ. Nộp tất cả từ đúng = điểm tối đa.
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="blue"><Zap size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>{t('waitingRoom.gameIntroTitle')}</div>
                      <p className={styles.gameInfoText}>
                        {round.game_type === 'quiz' ? t('waitingRoom.quizIntro') : t('waitingRoom.wordScrambleIntro')}
                      </p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="green"><BookOpen size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>{t('waitingRoom.rulesTitle')}</div>
                      <p className={styles.gameInfoText}>{t('waitingRoom.rules')}</p>
                    </div>
                  </div>
                  <div className={styles.gameInfoCard}>
                    <div className={styles.gameInfoIconWrap} data-color="gold"><Trophy size={18} /></div>
                    <div className={styles.gameInfoCardBody}>
                      <div className={styles.gameInfoTitle}>{t('waitingRoom.scoringTitle')}</div>
                      <p className={styles.gameInfoText}>{t('waitingRoom.scoringExplain')}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className={styles.waitMessage}>
            {t('waitingRoom.waitMessage')}
          </div>

          <div className={styles.participantsSection}>
            <div className={styles.participantHeader}>
              <Users size={15} />
              <span className={styles.participantTitle}>{t('waitingRoom.playerCount', { n: participants.length })}</span>
              <span className={styles.participantBadge}>{participants.length}</span>
            </div>
            <div className={styles.participantScroll}>
              <div className={styles.participantGrid}>
                {participants.map((p) => (
                  <div key={p.participant_id} className={styles.participantChip}>
                    <Avatar src={p.avatar_url ?? undefined} name={p.display_name ?? '?'} size="sm" />
                    <span className={styles.chipName}>{p.display_name ?? t('waitingRoom.fallbackPlayerName')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Button variant="ghost" leftIcon={<LogOut size={16} />} onClick={handleLeave}>
            {t('waitingRoom.leaveRoom')}
          </Button>
        </div>

        <div className={styles.sidePanel}>
          <div className={styles.notifPanel}>
            <div className={styles.notifHeader}>
              <Bell size={14} />
              <span>{t('common.notification')}</span>
              {notifications.length > 0 && (
                <span className={styles.notifCount}>{notifications.length}</span>
              )}
            </div>
            <div className={styles.notifList}>
              {notifications.length === 0 ? (
                <div className={styles.notifEmpty}>{t('common.noNotification')}</div>
              ) : (
                [...notifications].reverse().map((n: GameNotification) => (
                  <div key={n.id} className={`${styles.notifItem} ${styles[`notif_${n.type}`]}`}>
                    <span className={styles.notifIcon}>
                      {n.type === 'join' ? <UserPlus size={10} /> : n.type === 'leave' ? <UserMinus size={10} /> : n.type === 'countdown' ? <Timer size={10} /> : <Info size={10} />}
                    </span>
                    <span className={styles.notifMsg}>{n.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <ChatPanel roundId={roundId} theme="dark" />
        </div>
      </div>
    </div>
  );}